import { Menu, MenuItem, Typography } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange, TimeFormat, TimeRangeInputValue } from '@chronosphereio/core';
import { useTimeZone } from '@/components/context';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { formatTimeRangeInputValue } from '../utils';

export interface CopyTimeRangeMenuProps {
  /**
   * The anchor element for the menu positioning
   */
  anchorEl: HTMLButtonElement | null;
  /**
   * Whether the menu is open
   */
  open: boolean;
  /**
   * Callback to close the menu
   */
  onClose: () => void;
  /**
   * The current time range (may be relative or absolute)
   */
  timeRange: TimeRangeInputValue;
  /**
   * The absolute version of the time range
   */
  absoluteTimeRange: AbsoluteTimeRange;
  /**
   * Callback when copying with absolute time range
   */
  onCopyAbsolute: () => void;
  /**
   * Callback when copying with relative time range
   */
  onCopyRelative: () => void;
  /**
   * Accessibility label for the menu
   */
  ariaLabel: string;
  /**
   * Tweaks the wording of the menu to remove "with" in the menu options
   */
  copyingJustTimestamp?: boolean;
  /**
   * Optional time zone override. If provided, this will be used instead of the context value.
   */
  timeZone?: string;
  /**
   * Optional time format override. If provided, this will be used instead of the context value.
   */
  timeFormat?: TimeFormat;
}

/**
 * Reusable menu component for choosing between copying with absolute or relative time range.
 * Displays formatted time ranges as descriptions for each menu item.
 */
export function CopyTimeRangeMenu({
  anchorEl,
  open,
  onClose,
  timeRange,
  absoluteTimeRange,
  onCopyAbsolute,
  onCopyRelative,
  ariaLabel,
  timeZone: timeZoneProp,
  timeFormat: timeFormatProp,
  copyingJustTimestamp = false,
}: CopyTimeRangeMenuProps) {
  const { timeZone: contextTimeZone } = useTimeZone();
  const userTimeFormat = useUserTimeFormat();

  // Use props if provided, otherwise fall back to context
  const timeZone = timeZoneProp ?? contextTimeZone;
  const timeFormat = timeFormatProp ?? userTimeFormat;

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose} ariaLabel={ariaLabel}>
      <MenuItem
        onClick={() => {
          onCopyRelative();
          onClose();
        }}
      >
        <div>
          <div>Copy {copyingJustTimestamp ? '' : 'with '}relative time range</div>
          <Typography display={'block'} variant="body2" color="text.legacySecondary">
            {formatTimeRangeInputValue({ timeRange, timeFormat, timeZone })}
          </Typography>
        </div>
      </MenuItem>
      <MenuItem
        onClick={() => {
          onCopyAbsolute();
          onClose();
        }}
      >
        <div>
          <div>Copy {copyingJustTimestamp ? '' : 'with '}absolute time range</div>
          <Typography display={'block'} variant="body2" color="text.legacySecondary">
            {formatTimeRangeInputValue({ timeRange: absoluteTimeRange, timeFormat, timeZone })}
          </Typography>
        </div>
      </MenuItem>
    </Menu>
  );
}
